package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.FriendSharedPlace
import fyi.moko.hotpl.domain.service.SocialGraphService
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * Every friend's shared places, live, keyed by the friend's uid. One
 * [SocialGraphService.observeSharedPlaces] listener per current friend, re-wired whenever
 * the friends stream changes (add/remove a friend), so the Friends tab, the mutual-wish
 * cards and the Journal badge all read from the same real-time source.
 *
 * Emits only once every friend's listener has delivered its first snapshot — a friend
 * missing from the map is therefore "not a friend any more", never "still loading". A
 * friend whose listener fails contributes an empty list (their row reads as no shared
 * places); a failure of the friends stream itself degrades to an empty map. Online-only,
 * like everything social: no Room cache, nothing written.
 */
class ObserveFriendsSharedPlacesUseCase(
    private val observeFriends: ObserveFriendsUseCase,
    private val socialGraphService: SocialGraphService,
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    operator fun invoke(): Flow<Map<String, List<FriendSharedPlace>>> =
        observeFriends()
            .map { friends -> friends.map { it.uid }.distinct() }
            .flatMapLatest { uids ->
                if (uids.isEmpty()) {
                    flowOf(emptyMap())
                } else {
                    combine(uids.map { uid -> sharedPlacesOf(uid) }) { lists ->
                        uids.zip(lists.toList()).toMap()
                    }
                }
            }
            .catch { e ->
                if (e is CancellationException) throw e
                emit(emptyMap())
            }

    private fun sharedPlacesOf(uid: String): Flow<List<FriendSharedPlace>> =
        socialGraphService.observeSharedPlaces(uid)
            .map { places -> places.sortedByDescending { it.updatedAt } }
            .catch { e ->
                if (e is CancellationException) throw e
                println("ObserveFriendsSharedPlaces: $uid's places unavailable: ${e.message ?: e::class.simpleName}")
                emit(emptyList())
            }
}
